import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { BMWindowClient } from '@amplify/web-assessment-common/dist/bm_window';
import {
    MEASURE_BY_SKILL_NAME,
    SKILL_NAMES_BY_APPLICATION,
    SKILL_NAMES,
} from '../../../constants/assessments';
import { PERIOD } from '../../../constants/enrollment';
import { TRC_INTERPRETATIONS } from '../../../constants/interpretations';
import AssessLink from '../assess-link/AssessLink';
import TRCDetails from '../trc-details/TRCDetails';
import { studentType } from '../../../constants/prop-types';
import {
    constructTRCResumeLink,
    navigateToTRCDetails,
    navigateToTRCBMAssessment,
} from '../../../common/navigation/navigation';
import { getOfficialnessInfo } from '../../../common/utils/studentsListHelpers';
import { useAppContext } from '../../../common/contexts/AppContext';
import { usePopupContext } from '../../../common/contexts/PopupContext';
import { useTRCAppContext } from '../../../common/contexts/TRCAppContext';
import { DASHBOARD_PARAMETERS } from '../../../constants/dashboard';
import { localize } from '../../../common/localization';
import useAssessIcons from '../../../common/hooks/useAssessIcons';
import TableHeader from '../table-header/TableHeader';
import { useAppConfigContext } from '../../../common/contexts/AppConfigContext';
import { POPUP_NAMES } from '../../../constants/popups';
import getAvailableBenchmarkPeriods from '../../../common/utils/getAvailableBenchmarkPeriods';

/**
 * Calculate assessable result from official or unofficial result by rule:
 * Official - If official result owned by current user
 * Unofficial - if unofficial result exists and official is paused or unofficial is not
 * No assessable result available otherwise
 * @param officialResultInfo
 * @param unofficialResultInfo
 * @param staffId
 * @returns result info with result details
 */
const getAssessableResult = (officialResultInfo, unofficialResultInfo, staffId) => {
    if (officialResultInfo.staffId === staffId) {
        return officialResultInfo;
    }
    if (unofficialResultInfo && (officialResultInfo.isPaused || !unofficialResultInfo.isPaused)) {
        return unofficialResultInfo;
    }
    return undefined;
};
/**
 * Return url params if assessable result exists or undefiled if no result details available
 * @param officialResultInfo
 * @param unofficialResultInfo
 * @param staffId
 * @param params
 * @returns {{probeResultId: *, book: *, page: *, officialnessRank: *}}
 * plus {{studentId: *, period *, classeId: *}} from {params}
 */
export const getNavigationDetails = (officialResultInfo, unofficialResultInfo, staffId, params) => {
    const assessableResult = getAssessableResult(officialResultInfo, unofficialResultInfo, staffId);
    if (assessableResult) {
        const navigationParams = {
            ...params,
            probeResultId: assessableResult.id,
            page: assessableResult.page,
            book: assessableResult.book,
        };
        if (assessableResult.officialnessRank) {
            navigationParams.officialnessRank = assessableResult.officialnessRank;
        }
        return navigationParams;
    }
    return undefined;
};

export const GetTRCDetailsForPeriod = (
    studentDetails,
    period,
    staffId,
    classId,
    showOfficialnessPopup,
    showCreatePresumedResultPopup,
    showDeletePresumedResultPopup,
    getPresumedResultSourceDetails,
    isBMAssessmentIconEnabled,
    isBMAssessmentIconVisible,
    application,
    showTRCOutsideBMPopup,
) => {
    const trcDetails = studentDetails.appStatuses[application][period];
    const officialResultInfo = trcDetails.resultInfo.officialResult;
    const unofficialResultInfo = trcDetails.resultInfo.unofficialResult;
    const params = {
        studentId: studentDetails.id,
        classeId: classId,
        period,
        application,
    };
    const { bmWindowConfig, isDemoClassSelected, language } = useAppContext();
    const [isDisplayTRCOutsideBMPopup, setDisplayTRCOutsideBMPopup] = useState(false);
    const BMWindow = new BMWindowClient();

    useEffect(() => {
        BMWindow.isCurrentDayInPeriod(language, period, application).then(isCurrentDay => {
            setDisplayTRCOutsideBMPopup(
                !isCurrentDay && !isDemoClassSelected && bmWindowConfig === 'strict',
            );
        });
    }, [BMWindow, isDemoClassSelected, bmWindowConfig, language, period, application]);

    if (trcDetails.interp === TRC_INTERPRETATIONS.UNKNOWN) {
        if (isBMAssessmentIconVisible(period)) {
            const startAssessment = () => {
                const presumedResultSourceDetails = getPresumedResultSourceDetails(
                    studentDetails.id,
                    period,
                    studentDetails.grade,
                    application,
                );
                const displayCreatePresumedPopup = () =>
                    showCreatePresumedResultPopup({
                        ...presumedResultSourceDetails,
                        classId,
                        studentId: studentDetails.id,
                        grade: studentDetails.grade,
                        currentPeriod: period,
                        studentName: studentDetails.firstName,
                        application,
                    });
                if (presumedResultSourceDetails) {
                    return displayCreatePresumedPopup;
                }
                return navigateToTRCBMAssessment(params);
            };

            const assessLinkMode = isBMAssessmentIconEnabled(period)
                ? AssessLink.MODE.REQUIRED
                : AssessLink.MODE.DISABLED;

            return <AssessLink mode={assessLinkMode} onClickHandler={startAssessment()} />;
        }
        return <div />;
    }

    const navigationDetails = getNavigationDetails(
        officialResultInfo,
        unofficialResultInfo,
        staffId,
        params,
        application,
    );

    let displayOfficialnessPopup = null;
    let officialnessInfo = null;
    let onNavigateToResult = null;
    let displayDeletePresumedResultPopup = null;
    let displayTRCOutsideBMPopup = null;
    // if official result is not related to current staff, we need to display official popup
    const isOfficialResultNotOwnedByCurrentStaff = officialResultInfo.staffId !== staffId;
    // and if this official result is paused, we need to display official popup only if there is no unofficial result
    const isOfficialPopupNeeded = officialResultInfo.isPaused ? !unofficialResultInfo : true;

    if (isOfficialResultNotOwnedByCurrentStaff && isOfficialPopupNeeded) {
        officialnessInfo = getOfficialnessInfo(
            studentDetails,
            officialResultInfo,
            unofficialResultInfo,
        );
        onNavigateToResult = navigateToTRCDetails;
        displayOfficialnessPopup = () =>
            showOfficialnessPopup(officialnessInfo, navigationDetails, onNavigateToResult);
    }

    if (trcDetails.interp === TRC_INTERPRETATIONS.INCOMPLETE) {
        const assessLinkMode = isBMAssessmentIconEnabled(period)
            ? AssessLink.MODE.RESUME
            : AssessLink.MODE.RESUME_DISABLED;
        return (
            <AssessLink
                mode={assessLinkMode}
                path={constructTRCResumeLink(navigationDetails)}
                onClickHandler={displayOfficialnessPopup}
            />
        );
    }

    if (trcDetails.isPresumed) {
        if (isDisplayTRCOutsideBMPopup) {
            displayTRCOutsideBMPopup = () => {
                showTRCOutsideBMPopup({
                    name: POPUP_NAMES.TRC_OUTSIDE_BM_POPUP,
                    period,
                });
            };
        } else {
            displayDeletePresumedResultPopup = () => {
                showDeletePresumedResultPopup({
                    classId,
                    studentId: studentDetails.id,
                    period,
                    grade: studentDetails.grade,
                    studentName: studentDetails.firstName,
                    application,
                });
            };
        }
    }

    return (
        <TRCDetails
            {...trcDetails}
            navigationDetails={navigationDetails}
            officialnessInfo={officialnessInfo}
            displayOfficialnessPopup={displayOfficialnessPopup}
            displayDeletePresumedResultPopup={displayDeletePresumedResultPopup}
            displayTRCOutsideBMPopup={displayTRCOutsideBMPopup}
        />
    );
};

const TRCSummary = ({ student, classId }) => {
    const trcApp = useTRCAppContext();
    const {
        staff: { id: staffId },
        applicationSpecificCallbacks: {
            [trcApp]: {
                [DASHBOARD_PARAMETERS.GET_PRESUMED_RESULT_SOURCE_DETAILS_CALLBACK]: getPresumedResultSourceDetails,
            },
        },
        fourthPeriodAvailability,
        selectedAppGroup,
    } = useAppContext();
    const {
        actions: {
            showOfficialnessPopup,
            showCreatePresumedResultPopup,
            showDeletePresumedResultPopup,
            showTRCOutsideBMPopup,
        },
    } = usePopupContext();

    const benchmarkPeriods = useMemo(
        () => getAvailableBenchmarkPeriods(selectedAppGroup, fourthPeriodAvailability),
        [fourthPeriodAvailability, selectedAppGroup],
    );

    const { isBMAssessmentIconEnabled, isBMAssessmentIconVisible } = useAssessIcons(
        benchmarkPeriods,
    );
    const { appConfigs } = useAppConfigContext();

    const licensedSkill = (appConfigs?.[trcApp]?.licensedMeasures ?? []).find(
        licensedItem => licensedItem.skillName === SKILL_NAMES.TEXT_READING_COMPREHENSION,
    );

    if (!licensedSkill?.grades.includes(student.grade)) {
        return null;
    }
    const hasFourthPeriod = benchmarkPeriods.length > 3;

    return (
        <>
            <TableHeader columns={['TRC', ...benchmarkPeriods]} hasFourthPeriod={hasFourthPeriod} />
            <div className='dashboard-measure-scores' data-test-id="trc-measure-scores">
                {SKILL_NAMES_BY_APPLICATION[trcApp].map(skillName => (
                    <div
                        className={classNames('dashboard-measure-info', {
                            'dashboard-has-fourth-period': hasFourthPeriod,
                        })}
                        key={skillName}
                    >
                        <div className='dashboard-skill'>
                            <span className='dashboard-skill-name'>{localize(skillName)}</span>
                            <span className='dashboard-measure-name'>
                                {localize(MEASURE_BY_SKILL_NAME[skillName])}
                            </span>
                        </div>

                        {benchmarkPeriods.map(period =>
                            period === PERIOD.SUMMER ? (
                                <Fragment key={period} />
                            ) : (
                                <Fragment key={period}>
                                    {GetTRCDetailsForPeriod(
                                        student,
                                        period,
                                        staffId,
                                        classId,
                                        showOfficialnessPopup,
                                        showCreatePresumedResultPopup,
                                        showDeletePresumedResultPopup,
                                        getPresumedResultSourceDetails,
                                        isBMAssessmentIconEnabled,
                                        isBMAssessmentIconVisible,
                                        trcApp,
                                        showTRCOutsideBMPopup,
                                    )}
                                </Fragment>
                            ),
                        )}
                    </div>
                ))}
            </div>
            <div className='dashboard-empty-divider' />
        </>
    );
};

TRCSummary.propTypes = {
    student: studentType.isRequired,
    classId: PropTypes.string.isRequired,
};

export default TRCSummary;
